/**
 * PublishGatePage — the seam-4 pre-publish gate (D-21/D-23). Before a result is published back to
 * Novera, the lab runs canPublishToNovera LIVE: enter a patient id → GET /portal/publish-gate/:patientId
 * → a clear ALLOWED / BLOCKED decision (color + glyph + text). Default-closed: absent an active
 * `publication_novera` consent the gate is BLOCKED (never a silent bypass — médico-légal).
 *
 * Cloud-only: when the module is absent (route 404s) the screen renders the calm "indisponible"
 * panel. Full 5-state DoD; sans-droit is this screen's own ForbiddenState on resultat.publish.
 */
import { useState } from "react";
import {
  ErrorState,
  ForbiddenState,
  LoadingState,
  OfflineState,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { PP_CAPS, isModuleAbsent, usePublishGate } from "../api";
import { PageHeader } from "../components/PageHeader";
import { ModuleAbsentState } from "../components/ModuleAbsentState";
import { VerdictPanel } from "../components/VerdictPanel";
import { LookupForm } from "../components/LookupForm";

export function PublishGatePage() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const canPublishCap = useCan(PP_CAPS.resultatPublish);

  const [patientId, setPatientId] = useState("");
  const gate = usePublishGate(patientId, canPublishCap && online);

  if (!canPublishCap) return <ForbiddenState requiredCapabilities={[PP_CAPS.resultatPublish]} />;

  const header = (
    <PageHeader title={t("pp.publish.title")} subtitle={t("pp.publish.subtitle")} />
  );

  if (isModuleAbsent(gate.error)) {
    return (
      <Screen>
        {header}
        <ModuleAbsentState />
      </Screen>
    );
  }

  const allowed = gate.data?.can_publish ?? false;

  return (
    <Screen>
      {header}

      <LookupForm
        label={t("pp.publish.field.patientId")}
        hint={t("pp.publish.field.patientId.hint")}
        submitLabel={t("pp.publish.check")}
        loading={gate.isFetching}
        onSubmit={setPatientId}
      />

      {!online && patientId !== "" && gate.data === undefined ? (
        <OfflineState onRetry={() => void gate.refetch()} cached={false} />
      ) : gate.isLoading && patientId !== "" ? (
        <LoadingState variant="spinner" />
      ) : gate.isError ? (
        <ErrorState
          message={gate.error?.message ?? ""}
          {...(gate.error?.code ? { code: gate.error.code } : {})}
          onRetry={() => void gate.refetch()}
        />
      ) : gate.data && patientId !== "" ? (
        <VerdictPanel
          title={t("pp.publish.gate.title")}
          patientId={patientId}
          tone={allowed ? "success" : "danger"}
          glyph={allowed ? "✓" : "✕"}
          verdictLabel={t(allowed ? "pp.publish.allowed" : "pp.publish.blocked")}
          description={t(allowed ? "pp.publish.allowed.desc" : "pp.publish.blocked.desc")}
          note={t("pp.consent.live.note")}
        />
      ) : null}
    </Screen>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>;
}
